/** The second level: rock, carved into chambers and the corridors between them.
 *
 * The surface is grown - zones flood outwards until they meet and the whole
 * plane is land or water. The underground is the opposite operation: the plane
 * starts as solid `rock`, which `TERRAIN_COST` prices as impassable, and the
 * generator *removes* it. That is why none of `biomes.ts` is reused here. A
 * cave grown the way a biome is grown produces open ground with rock islands in
 * it, which reads as a badly lit surface map rather than as a cavern.
 *
 * The one structural guarantee this file makes, and the reason the corridor
 * pass is a spanning tree rather than a scatter: **the carved floor is exactly
 * one eight-connected component**. Anything the flood at the end cannot reach
 * is filled back in with rock, so a chamber that failed to link up stops
 * existing rather than becoming a pocket the generator would later hide an
 * artifact in.
 *
 * The result is a plain single-level `AdventureMapState`. `generate.ts` runs
 * the ordinary placement passes over it and only then merges the two planes
 * into the two-level map the kernel is handed, which is what keeps every pass
 * in this package addressing cells as `y * width + x`.
 */

import type { AdventureMapState } from "@game/core";
import { createMap, setTerrain } from "@game/core";
import type { Zone } from "./biomes.js";
import { setVariants } from "./biomes.js";
import { UNDERGROUND_FLOORS } from "./catalog.js";
import type { Grid } from "./grid.js";
import {
  cheapestPath, chebyshev, flood, idx, orthoNeighbours, walkable, xOf, yOf,
} from "./grid.js";
import { type Rng, int, pick, shuffle } from "./rng.js";

export interface Underworld {
  /** A one-level map: `rock` everywhere except the carved floor. */
  readonly map: AdventureMapState;
  /** One per chamber, shaped as a `Zone` so the placement passes are shared. */
  readonly zones: readonly Zone[];
  /** Every walkable cell, ascending. */
  readonly floor: readonly number[];
}

/** Keep the outermost ring solid: a cave that runs off the edge reads as a hole. */
const MARGIN = 2;

/** Deterministic per-cell noise, so a corridor wanders without an rng roll per step. */
function cellNoise(i: number): number {
  let h = (i + 0x85ebca6b) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 0x27d4eb2f) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x165667b1) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

export function carveUnderworld(width: number, height: number, r: Rng): Underworld {
  const map = createMap(width, height, 1, { terrain: "rock" });
  const g: Grid = { width, height };
  const n = width * height;

  // Roughly one chamber per 300 cells, which puts 8 on a 48x48 map and 17 on a
  // 72x72 one - dense enough that a corridor is a short walk, sparse enough
  // that the rock between them still reads as rock.
  const wanted = Math.max(4, Math.min(28, Math.round(n / 300)));
  const seeds = chamberSeeds(g, r, wanted);
  if (seeds.length === 0) return { map, zones: [], floor: [] };

  const owner = new Int32Array(n).fill(-1);
  const zones: Zone[] = seeds.map((seed, id) => ({
    id, role: "neutral" as const, player: -1, seed,
    terrain: pick(r, UNDERGROUND_FLOORS) ?? "cavern",
    cells: [],
    job: "treasure" as const, theme: null,
  }));

  for (const z of zones) growChamber(g, r, z, owner, 46 + int(r, 70));
  linkChambers(g, r, zones, owner);
  roughenWalls(g, r, zones, owner);

  for (const z of zones) {
    for (const i of z.cells) setTerrain(map, xOf(g, i), yOf(g, i), 0, z.terrain);
  }

  const kept = pruneToOneComponent(map, g, zones);
  setVariants(map);
  return { map, zones, floor: kept };
}

/**
 * Chamber centres, spread apart by rejection sampling with a separation that
 * relaxes as attempts run out - the same shape `biomes.ts` uses for its zone
 * seeds, so a map that cannot fit the requested count degrades instead of
 * looping.
 */
function chamberSeeds(g: Grid, r: Rng, count: number): number[] {
  const out: number[] = [];
  const span = Math.min(g.width, g.height);
  const wantSep = Math.max(5, Math.trunc(span / 5));
  const lo = MARGIN + 2;
  const hiX = g.width - MARGIN - 3, hiY = g.height - MARGIN - 3;
  if (hiX <= lo || hiY <= lo) return out;

  for (let k = 0; k < count; k++) {
    let chosen = -1;
    for (let attempt = 0; attempt < 160 && chosen < 0; attempt++) {
      const i = idx(g, lo + int(r, hiX - lo + 1), lo + int(r, hiY - lo + 1));
      const sep = Math.max(3, wantSep - Math.trunc(attempt / 30));
      if (out.every((o) => chebyshev(g, o, i) >= sep)) chosen = i;
    }
    if (chosen >= 0) out.push(chosen);
  }
  return out;
}

/**
 * Grow one chamber by eating outwards from a frontier picked at random rather
 * than in queue order. A queue produces a disc; a random frontier produces a
 * lobed blob, which is what a cave chamber looks like.
 */
function growChamber(
  g: Grid, r: Rng, zone: Zone, owner: Int32Array, budget: number,
): void {
  if (owner[zone.seed] !== -1) return;
  owner[zone.seed] = zone.id;
  zone.cells.push(zone.seed);
  const frontier: number[] = [zone.seed];
  const buf: number[] = [];

  while (zone.cells.length < budget && frontier.length > 0) {
    const at = int(r, frontier.length);
    const cur = frontier[at]!;
    frontier[at] = frontier[frontier.length - 1]!;
    frontier.pop();
    for (const nb of orthoNeighbours(g, cur, buf)) {
      if (owner[nb] !== -1) continue;
      const x = xOf(g, nb), y = yOf(g, nb);
      if (x < MARGIN || y < MARGIN || x >= g.width - MARGIN || y >= g.height - MARGIN) continue;
      // Not every neighbour: a chamber that takes all four every time is a
      // rectangle with the corners knocked off.
      if (int(r, 100) >= 72) continue;
      owner[nb] = zone.id;
      zone.cells.push(nb);
      frontier.push(nb);
      if (zone.cells.length >= budget) break;
    }
  }
}

/**
 * A minimum spanning tree over the chamber centres, cut as corridors. Every
 * chamber is therefore reachable from every other before a single object is
 * placed, which is the guarantee the audit later has to hold up.
 */
function linkChambers(
  g: Grid, r: Rng, zones: readonly Zone[], owner: Int32Array,
): void {
  if (zones.length < 2) return;
  const parent = zones.map((_, i) => i);
  const find = (i: number): number => {
    let x = i;
    while (parent[x] !== x) { parent[x] = parent[parent[x]!]!; x = parent[x]!; }
    return x;
  };

  const edges: { a: number; b: number; d: number }[] = [];
  for (let a = 0; a < zones.length; a++) {
    for (let b = a + 1; b < zones.length; b++) {
      edges.push({ a, b, d: chebyshev(g, zones[a]!.seed, zones[b]!.seed) });
    }
  }
  edges.sort((p, q) => p.d - q.d || p.a - q.a || p.b - q.b);

  const cost = (i: number): number => {
    const x = xOf(g, i), y = yOf(g, i);
    if (x < MARGIN || y < MARGIN || x >= g.width - MARGIN || y >= g.height - MARGIN) return -1;
    return owner[i] !== -1 ? 2 : 10 + (cellNoise(i) % 11);
  };

  const buf: number[] = [];
  for (const e of edges) {
    const ra = find(e.a), rb = find(e.b);
    if (ra === rb) continue;
    const za = zones[e.a]!, zb = zones[e.b]!;
    const path = cheapestPath(g, za.seed, zb.seed, cost);
    if (path === null) continue;
    parent[ra] = rb;

    for (const i of path) {
      const half = chebyshev(g, i, za.seed) <= chebyshev(g, i, zb.seed) ? za : zb;
      claim(i, half, owner);
      // A one-cell corridor for its whole length reads as a pipe. Widening
      // about half the cells by one gives a passage that opens and narrows.
      if (int(r, 100) < 46) {
        const spots = shuffle(r, orthoNeighbours(g, i, buf).slice());
        for (const nb of spots) {
          const x = xOf(g, nb), y = yOf(g, nb);
          if (x < MARGIN || y < MARGIN || x >= g.width - MARGIN || y >= g.height - MARGIN) continue;
          claim(nb, half, owner);
          break;
        }
      }
    }
  }
}

function claim(i: number, zone: Zone, owner: Int32Array): void {
  if (owner[i] !== -1) return;
  owner[i] = zone.id;
  zone.cells.push(i);
}

/**
 * Bite single cells out of the rock face. Without this every chamber wall is
 * the outline of the growth pass, and growth passes leave long smooth arcs.
 */
function roughenWalls(
  g: Grid, r: Rng, zones: readonly Zone[], owner: Int32Array,
): void {
  const buf: number[] = [];
  const bites: { cell: number; zone: Zone }[] = [];
  for (const z of zones) {
    for (const i of z.cells) {
      for (const nb of orthoNeighbours(g, i, buf)) {
        if (owner[nb] !== -1) continue;
        const x = xOf(g, nb), y = yOf(g, nb);
        if (x < MARGIN || y < MARGIN || x >= g.width - MARGIN || y >= g.height - MARGIN) continue;
        if (int(r, 100) >= 14) continue;
        bites.push({ cell: nb, zone: z });
      }
    }
  }
  for (const b of bites) claim(b.cell, b.zone, owner);
}

/**
 * Paint the carved cells, then keep only the component the first chamber is in.
 * A pocket the corridor pass failed to reach is filled back to rock: an
 * unreachable cave is indistinguishable from solid rock to the player, and
 * leaving it open would let the object pass hide a bank inside it.
 */
function pruneToOneComponent(
  m: AdventureMapState, g: Grid, zones: readonly Zone[],
): number[] {
  const first = zones[0]?.seed;
  if (first === undefined) return [];
  const seen = flood(m, first);
  const kept: number[] = [];
  for (let i = 0; i < seen.length; i++) {
    if (seen[i] === 1) { kept.push(i); continue; }
    if (walkable(m, i)) setTerrain(m, xOf(g, i), yOf(g, i), 0, "rock");
  }
  for (const z of zones) {
    const live = z.cells.filter((i) => seen[i] === 1);
    z.cells.length = 0;
    for (const i of live) z.cells.push(i);
  }
  return kept;
}
